import { type DeferredPromise } from '@lyka/utils/src/defer'
import type flagsmith from 'flagsmith'
import { atom } from 'nanostores'
import { useExperimentStartedEvent } from './events/experiments'

declare global {
  interface Window {
    flagsmith?: typeof flagsmith
    experiments: DeferredPromise<Map<string, string>>
  }
}

export type ExperimentName = `experiment-${string}`
export type ExperimentVariant = string

export const isExperimentName = (value: unknown): value is ExperimentName => {
  return typeof value === 'string' && value.startsWith('experiment-')
}

export type Experiments = Map<string, string | boolean | number>

interface State {
  loaded: boolean
  error: boolean
  experiments: Experiments
}

const state = (): State => ({
  loaded: false,
  error: false,
  experiments: new Map(),
})

type Flags = Record<string, unknown>

export const $experiments = atom(state())

export const CONTROL_VARIANT = 'control' as const
export const DEFAULT_EXPERIMENT_VARIANT = 'experiment' as const

// Trigger an event when an experiment starts
const triggerExperimentStartedEvent = (experimentName: ExperimentName, experimentVariant: ExperimentVariant): void => {
  useExperimentStartedEvent().send({
    experimentName,
    experimentVariant,
  })
}

const EXPERIMENT_PREFIX = 'experiment-' as const

export const fromFlags = (flags: Flags): Experiments => {
  const experiments = new Map()

  for (const flag in flags) {
    if (flag.startsWith(EXPERIMENT_PREFIX)) {
      const variant = flags[flag]

      experiments.set(flag, variant)
    }
  }

  return experiments
}

const PERSISTENCE_KEY = 'experiments' as const

export const fromPersisted = (): Experiments => {
  const object = localStorage.getItem(PERSISTENCE_KEY)

  const experiments = new Map()

  if (!object) {
    return experiments
  }

  try {
    const parsed = JSON.parse(object)

    for (const [key, value] of Object.entries(parsed)) {
      experiments.set(key, value)
    }
  } catch {}

  return experiments
}

export const toPersisted = (experiments: Experiments, existing = fromPersisted()): void => {
  // Merge the existing experiments with the new ones
  const merged = new Map([...existing, ...experiments])

  // Convert to an object for storage
  const object = Object.fromEntries(merged)

  // Store in localStorage
  localStorage.setItem(PERSISTENCE_KEY, JSON.stringify(object))
}

export const fromParams = (url: string): Experiments => {
  const experiments = new Map()

  const params = new URL(url).searchParams

  for (const [key, value] of params.entries()) {
    if (isExperimentName(key)) {
      const variant = !value ? DEFAULT_EXPERIMENT_VARIANT : value

      experiments.set(key, variant)
    }
  }

  return experiments
}

// For testing
export const resetExperiments = (): void => {
  localStorage.removeItem(PERSISTENCE_KEY)
  $experiments.set(state())
}

export const innerJoinExperiments = (initial: Experiments, overrides: Experiments): Experiments => {
  const joined = new Map()

  for (const [key, value] of overrides) {
    if (initial.has(key)) {
      joined.set(key, value)
    }
  }

  return joined
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useExperiments = () => {
  const fail = (): void => {
    if ($experiments.get().loaded) {
      return
    }

    const experiments: Experiments = new Map()

    $experiments.set({
      error: true,
      loaded: true,
      experiments,
    })
  }

  const init = (flags: Flags) => {
    const flagged = fromFlags(flags)
    const params = fromParams(location.href)
    const persisted = fromPersisted()

    if (params.size) {
      toPersisted(params)
    }

    const overrides = innerJoinExperiments(flagged, new Map([...persisted, ...params]))

    // Only keep experiments that are both flagged and overridden
    const joined = new Map([...flagged, ...overrides])

    $experiments.set({
      error: false,
      loaded: true,
      experiments: joined,
    })
  }

  const ready = (): Promise<Experiments> => {
    return new Promise((resolve) => {
      $experiments.subscribe(({ experiments, loaded }) => {
        if (loaded) {
          resolve(experiments)
        }
      })
    })
  }

  const getVariant = async (experimentName: ExperimentName): Promise<string | boolean | number | undefined> => {
    const experiments = await ready()

    const variant = experiments.get(experimentName)

    if (variant === undefined) {
      return undefined
    }

    triggerExperimentStartedEvent(experimentName, String(variant))

    return variant
  }

  const inVariant = async (experimentName: ExperimentName, experimentVariant: ExperimentVariant): Promise<boolean> => {
    const variant = await getVariant(experimentName)
    const active = variant === experimentVariant

    return active
  }

  return {
    fail,
    inVariant,
    getVariant,
    ready,
    init,
  }
}
